import { z } from 'zod';
import { UserRegionSchema } from './onboarding';
import { PaginatedResponseSchema } from './pagination';

/**
 * Блок 11 «Личный кабинет», экран 2.11.3 — контракт биллинга (планы / промо-preview /
 * checkout / история). ADR-0093, PR `feature/block-11-subscription` (фаза 11.C,
 * фронт-ферст на MSW-моках). Любое изменение → новый ADR (P-011).
 *
 * ── ИНВАРИАНТ БЕЗОПАСНОСТИ (ДЕНЬГИ, P-006/P-003) ─────────────────────────────
 * **Фронт НИКОГДА не шлёт сумму.** `QuoteInput`/`CheckoutInput` несут только
 * `planCode` + `promoCode?` — НЕТ `amount`/`currency`/`price`. Сумму и скидку
 * считает бэк по `pricing_plans`/`promo_campaigns` (P-003). Type-level защита:
 * `z.infer<QuoteInput>`/`z.infer<CheckoutInput>` физически не могут нести `amount`
 * (см. `@ts-expect-error` в `billing.test.ts`); лишние поля strip'аются (default
 * ZodObject — паттерн `analytics.ts`/`cooperation.ts`/`profile.ts`) ДО сети, так
 * что даже багнутый caller не отправит сумму на провод.
 *
 * **Провайдер выбирает сервер** по `user.region` из JWT — НЕ из тела. В теле
 * запросов провайдера нет; `provider` присутствует только в `GET plans`-ответе
 * (для показа «оплата через Kaspi/ЮKassa»).
 *
 * ── Деньги — целые в ОСНОВНОЙ единице валюты ────────────────────────────────
 * 650 = 650 ₽, 3250 = 3250 ₸, 84500 = 84500 сум (PRD). Без float — промо 30%/50%
 * на эти цены дают целые. `nonnegative()` — сумма не может быть отрицательной.
 *
 * ── `planCode`/`promoCode` — `z.string()` (НЕ enum) ─────────────────────────
 * Коды определяются бэком (`pricing_plans`/`promo_campaigns`); фронт echo'ит код
 * из `GET plans` и шлёт обратно. НЕ выдумывать перечень (анти-додумывание,
 * CLAUDE.md): новые тарифы/промо добавляются на бэке без правки контракта.
 *
 * ── Webhook — бэкенд-онли (НЕ здесь) ────────────────────────────────────────
 * `POST /api/billing/webhook/{provider}` (провайдер → сервер; подпись P-005 ДО
 * парсинга тела) — провайдер-специфичный, детальный контракт с интеграцией бэка.
 * В `billing.ts` фиксируем только фронт-контракты (plans/quote/checkout/history).
 *
 * References:
 *   - ADR-0093 §1/§2; PRD `15-block-11-cabinet.md` §2.11.3; `01-foundation.md` §1.7
 *   - `UserRegionSchema`: `contracts/onboarding.ts`; `PaginatedResponseSchema`: `contracts/pagination.ts`
 *   - паттерн input-анти-спуфинг: `contracts/profile.ts`, `contracts/analytics.ts`
 *   - policies: P-003, P-004, P-005, P-006, P-009, P-011, P-012, P-013, P-024, P-001
 */

// ── Валюта (3 региона) ───────────────────────────────────────────────────────
export const BillingCurrencySchema = z.enum(['KZT', 'RUB', 'UZS']);
export type BillingCurrency = z.infer<typeof BillingCurrencySchema>;

// ── Провайдер (выбирает сервер по region; в теле запросов НЕТ — только в plans) ──
export const BillingProviderSchema = z.enum(['kaspi', 'yookassa']);
export type BillingProvider = z.infer<typeof BillingProviderSchema>;

// ── Статус платежа (история) ─────────────────────────────────────────────────
export const BillingPaymentStatusSchema = z.enum(['paid', 'pending', 'failed', 'refunded']);
export type BillingPaymentStatus = z.infer<typeof BillingPaymentStatusSchema>;

// ── Деньги — целое ≥ 0 в основной единице валюты (P-003; без float) ───────────
const MoneyAmountSchema = z.number().int().nonnegative();

// ── ISO 8601 (как me.ts: offset/Z) ──────────────────────────────────────────
const Iso8601Schema = z.string().datetime({ offset: true });

// ── 1. План (GET /api/billing/plans?region) ──────────────────────────────────
export const BillingPlanSchema = z.object({
  planCode: z.string(),
  region: UserRegionSchema,
  currency: BillingCurrencySchema,
  basePrice: MoneyAmountSchema,
  period: z.literal('monthly'),
  provider: BillingProviderSchema,
});
export type BillingPlan = z.infer<typeof BillingPlanSchema>;

// Envelope (месячная одна → массив из 1; envelope для forward-compat, паттерн `{ shops }`).
export const BillingPlansResponseSchema = z.object({
  plans: z.array(BillingPlanSchema),
});
export type BillingPlansResponse = z.infer<typeof BillingPlansResponseSchema>;

// ── 2. Промо-preview (POST /api/billing/quote) — тело БЕЗ суммы (P-006) ───────
// Default-strip ZodObject (НЕ .strict()): лишние поля (amount/currency/price)
// молча срезаются ДО сети — сервер их игнорирует и считает сам (P-003). Тип
// `QuoteInput` несёт только planCode/promoCode (type-level money-guard).
export const QuoteInputSchema = z.object({
  planCode: z.string().min(1),
  promoCode: z.string().min(1).optional(),
});
export type QuoteInput = z.infer<typeof QuoteInputSchema>;

export const QuoteResponseSchema = z.object({
  planCode: z.string(),
  currency: BillingCurrencySchema,
  baseAmount: MoneyAmountSchema,
  finalAmount: MoneyAmountSchema,
  promoApplied: z.boolean(),
  // 0..100; присутствует только когда промо применено (бэк решает валидность).
  discountPercent: z.number().int().min(0).max(100).optional(),
  // echo применённого кода (или отсутствует, если промо не применено).
  promoCode: z.string().optional(),
});
export type QuoteResponse = z.infer<typeof QuoteResponseSchema>;

// ── 3. Checkout (POST /api/billing/checkout) — тело БЕЗ суммы (P-006) ─────────
// 11.C — НЕ вызывается реально (кнопка «Оплатить» = заглушка). Контракт + клиент
// зафиксированы для билинг-бэка. Тело идентично quote (planCode + promoCode?).
export const CheckoutInputSchema = z.object({
  planCode: z.string().min(1),
  promoCode: z.string().min(1).optional(),
});
export type CheckoutInput = z.infer<typeof CheckoutInputSchema>;

export const CheckoutResponseSchema = z.object({
  paymentId: z.string().uuid(),
  // Редирект на провайдера (Kaspi/ЮKassa) — сервер формирует URL.
  checkoutUrl: z.string().url(),
});
export type CheckoutResponse = z.infer<typeof CheckoutResponseSchema>;

// ── 4. История платежей (GET /api/billing/history) — пагинированный список ────
export const BillingHistoryItemSchema = z.object({
  paymentId: z.string().uuid(),
  createdAt: Iso8601Schema,
  amount: MoneyAmountSchema,
  currency: BillingCurrencySchema,
  status: BillingPaymentStatusSchema,
  planCode: z.string(),
});
export type BillingHistoryItem = z.infer<typeof BillingHistoryItemSchema>;

export const BillingHistoryResponseSchema = PaginatedResponseSchema(BillingHistoryItemSchema);
export type BillingHistoryResponse = z.infer<typeof BillingHistoryResponseSchema>;
